import { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, Calendar, FileText, Users, Building2, Clock, 
  CheckCircle, AlertCircle, Download, Send, Edit, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MeetingManagement() {
  const { user } = useContext(AuthContext);
  const [scheduledMeetings, setScheduledMeetings] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [orgReports, setOrgReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateMeetingDialog, setShowCreateMeetingDialog] = useState(false);
  const [showCreateOrgDialog, setShowCreateOrgDialog] = useState(false);
  const [showEditOrgDialog, setShowEditOrgDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    meeting_date: '',
    meeting_time: '19:00',
    location: 'Google Meet',
    google_meet_link: '',
    reports_deadline: '',
    agenda_deadline: ''
  });
  const [newOrg, setNewOrg] = useState({
    name: '',
    short_name: '',
    contact_name: '',
    contact_email: '',
    description: ''
  });
  const [editOrg, setEditOrg] = useState({
    name: '',
    short_name: '',
    contact_name: '',
    contact_email: '',
    description: ''
  });

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [meetingsRes, orgsRes, reportsRes] = await Promise.all([
        axios.get(`${API}/scheduled-meetings`),
        axios.get(`${API}/organizations`),
        axios.get(`${API}/org-reports`)
      ]);
      setScheduledMeetings(meetingsRes.data);
      setOrganizations(orgsRes.data);
      setOrgReports(reportsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/scheduled-meetings`, newMeeting);
      toast.success('Meeting scheduled successfully');
      setShowCreateMeetingDialog(false);
      setNewMeeting({
        title: '',
        meeting_date: '',
        meeting_time: '19:00',
        location: 'Google Meet',
        google_meet_link: '',
        reports_deadline: '',
        agenda_deadline: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create meeting: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/organizations`, newOrg);
      toast.success('Organization created successfully');
      setShowCreateOrgDialog(false);
      setNewOrg({ name: '', short_name: '', contact_name: '', contact_email: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create organization: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditOrg = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/organizations/${selectedOrg.id}`, editOrg);
      toast.success('Organization updated successfully');
      setShowEditOrgDialog(false);
      setSelectedOrg(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update organization: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteOrg = async (orgId, orgName) => {
    if (!window.confirm(`Are you sure you want to deactivate ${orgName}?`)) return;
    try {
      await axios.put(`${API}/organizations/${orgId}`, { is_active: false });
      toast.success('Organization deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to deactivate organization: ' + (error.response?.data?.detail || error.message));
    }
  };

  const openEditOrgDialog = (org) => {
    setSelectedOrg(org);
    setEditOrg({
      name: org.name,
      short_name: org.short_name,
      contact_name: org.contact_name,
      contact_email: org.contact_email,
      description: org.description || ''
    });
    setShowEditOrgDialog(true);
  };

  const handleSendReminders = async (meetingId, meetingTitle, pendingCount) => {
    if (!window.confirm(`Send report reminders to ${pendingCount} organization(s) for "${meetingTitle}"?`)) return;
    try {
      toast.info('Sending reminders...');
      const response = await axios.post(`${API}/scheduled-meetings/${meetingId}/send-reminders`, {});
      const { sent_count, failed_count } = response.data;
      
      if (sent_count > 0) {
        toast.success(`${sent_count} reminder(s) sent successfully!`);
      }
      if (failed_count > 0) {
        toast.warning(`${failed_count} reminder(s) failed to send`);
      }
    } catch (error) {
      toast.error('Failed to send reminders: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSendSingleReminder = async (meetingId, orgId, orgName) => {
    if (!window.confirm(`Send report reminder to ${orgName}?`)) return;
    try {
      toast.info(`Sending reminder to ${orgName}...`);
      const response = await axios.post(`${API}/scheduled-meetings/${meetingId}/send-reminders`, {
        organization_id: orgId
      });
      const { sent_count } = response.data;
      
      if (sent_count > 0) {
        toast.success(`Reminder sent to ${orgName}!`);
      } else {
        toast.warning('No reminder sent - organization may have already submitted');
      }
    } catch (error) {
      toast.error('Failed to send reminder: ' + (error.response?.data?.detail || error.message));
    }
  };

  const downloadMeetingPack = async (meetingId) => {
    try {
      toast.info('Generating meeting pack...');
      const response = await axios.get(`${API}/scheduled-meetings/${meetingId}/pack`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `meeting_pack.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Meeting pack downloaded');
    } catch (error) {
      toast.error('Failed to generate meeting pack');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getMeetingStatus = (meeting) => {
    const meetingDate = new Date(meeting.meeting_date);
    const now = new Date();
    
    if (meeting.status === 'completed') return { label: 'Completed', color: 'bg-green-100 text-green-700' };
    if (meeting.status === 'cancelled') return { label: 'Cancelled', color: 'bg-red-100 text-red-700' };
    if (meetingDate < now) return { label: 'Past', color: 'bg-gray-100 text-gray-700' };
    
    const daysUntil = Math.ceil((meetingDate - now) / (1000 * 60 * 60 * 24));
    if (daysUntil <= 7) return { label: `${daysUntil}d away`, color: 'bg-orange-100 text-orange-700' };
    return { label: 'Scheduled', color: 'bg-blue-100 text-blue-700' };
  };

  const getReportStatus = (meetingId, orgId) => {
    const report = orgReports.find(r => r.scheduled_meeting_id === meetingId && r.organization_id === orgId);
    if (!report) return { status: 'pending', label: 'Not Started', color: 'text-gray-400' };
    if (report.status === 'submitted') return { status: 'submitted', label: 'Submitted', color: 'text-green-600' };
    if (report.status === 'draft') return { status: 'draft', label: 'Draft', color: 'text-orange-500' };
    return { status: 'pending', label: 'Pending', color: 'text-gray-400' };
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="meeting-management-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Meeting Management</h1>
          <p className="text-gray-600">Schedule meetings, manage agendas, and generate packs</p>
        </div>
      </div>

      <Tabs defaultValue="meetings" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="meetings">
            <Calendar className="w-4 h-4 mr-2" />
            Scheduled Meetings
          </TabsTrigger>
          <TabsTrigger value="organizations">
            <Building2 className="w-4 h-4 mr-2" />
            Partner Organizations
          </TabsTrigger>
          <TabsTrigger value="reports">
            <FileText className="w-4 h-4 mr-2" />
            Report Status
          </TabsTrigger>
        </TabsList>

        {/* Scheduled Meetings Tab */}
        <TabsContent value="meetings" className="space-y-6">
          <div className="flex justify-end">
            {isAdmin && (
              <Dialog open={showCreateMeetingDialog} onOpenChange={setShowCreateMeetingDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-blue-500 to-green-500" data-testid="schedule-meeting-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Schedule Meeting
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Schedule New Meeting</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateMeeting} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Meeting Title *</Label>
                      <Input
                        value={newMeeting.title}
                        onChange={(e) => setNewMeeting({...newMeeting, title: e.target.value})}
                        placeholder="HLRA Monthly Meeting - Month Year"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date *</Label>
                        <Input
                          type="date"
                          value={newMeeting.meeting_date}
                          onChange={(e) => setNewMeeting({...newMeeting, meeting_date: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Time</Label>
                        <Input
                          type="time"
                          value={newMeeting.meeting_time}
                          onChange={(e) => setNewMeeting({...newMeeting, meeting_time: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Location</Label>
                      <Input
                        value={newMeeting.location}
                        onChange={(e) => setNewMeeting({...newMeeting, location: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Google Meet Link</Label>
                      <Input
                        value={newMeeting.google_meet_link}
                        onChange={(e) => setNewMeeting({...newMeeting, google_meet_link: e.target.value})}
                        placeholder="https://meet.google.com/..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Agenda Deadline</Label>
                        <Input
                          type="date"
                          value={newMeeting.agenda_deadline}
                          onChange={(e) => setNewMeeting({...newMeeting, agenda_deadline: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Reports Deadline</Label>
                        <Input
                          type="date"
                          value={newMeeting.reports_deadline}
                          onChange={(e) => setNewMeeting({...newMeeting, reports_deadline: e.target.value})}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setShowCreateMeetingDialog(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-gradient-to-r from-blue-500 to-green-500">
                        Schedule Meeting
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {scheduledMeetings.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No meetings scheduled</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {scheduledMeetings.map((meeting) => {
                const status = getMeetingStatus(meeting);
                return (
                  <Card key={meeting.id} className="card-hover">
                    <CardContent className="py-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${status.color}`}>
                              {status.label}
                            </span>
                            {meeting.meeting_pack_generated && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">Pack Ready</span>
                            )}
                          </div>
                          <Link to={`/scheduled-meeting/${meeting.id}`}>
                            <h3 className="text-lg font-semibold text-gray-900 hover:text-blue-600">{meeting.title}</h3>
                          </Link>
                          <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                            <span className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {formatDate(meeting.meeting_date)} at {meeting.meeting_time}
                            </span>
                            <span className="flex items-center">
                              <Users className="w-4 h-4 mr-1" />
                              {meeting.location}
                            </span>
                          </div>
                          {meeting.reports_deadline && (
                            <p className="text-xs text-gray-500 mt-1">
                              Reports due: {formatDate(meeting.reports_deadline)} | Agenda closes: {formatDate(meeting.agenda_deadline)}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => downloadMeetingPack(meeting.id)}>
                            <Download className="w-4 h-4 mr-1" />
                            Pack
                          </Button>
                          <Link to={`/scheduled-meeting/${meeting.id}`}>
                            <Button variant="outline" size="sm">
                              <Edit className="w-4 h-4 mr-1" />
                              Manage
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Organizations Tab */}
        <TabsContent value="organizations" className="space-y-6">
          <div className="flex justify-end">
            {isAdmin && (
              <Dialog open={showCreateOrgDialog} onOpenChange={setShowCreateOrgDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-blue-500 to-green-500">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Organization
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Partner Organization</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateOrg} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Organization Name *</Label>
                        <Input
                          value={newOrg.name}
                          onChange={(e) => setNewOrg({...newOrg, name: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Short Name *</Label>
                        <Input
                          value={newOrg.short_name}
                          onChange={(e) => setNewOrg({...newOrg, short_name: e.target.value})}
                          placeholder="e.g., BH, RMG"
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Contact Name *</Label>
                        <Input
                          value={newOrg.contact_name}
                          onChange={(e) => setNewOrg({...newOrg, contact_name: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Email *</Label>
                        <Input
                          type="email"
                          value={newOrg.contact_email}
                          onChange={(e) => setNewOrg({...newOrg, contact_email: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={newOrg.description}
                        onChange={(e) => setNewOrg({...newOrg, description: e.target.value})}
                        rows={2}
                      />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setShowCreateOrgDialog(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-gradient-to-r from-blue-500 to-green-500">
                        Add Organization
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Edit Organization Dialog */}
          <Dialog open={showEditOrgDialog} onOpenChange={setShowEditOrgDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Partner Organization</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleEditOrg} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Organization Name *</Label>
                    <Input
                      value={editOrg.name}
                      onChange={(e) => setEditOrg({...editOrg, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Short Name *</Label>
                    <Input
                      value={editOrg.short_name}
                      onChange={(e) => setEditOrg({...editOrg, short_name: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Contact Name *</Label>
                    <Input
                      value={editOrg.contact_name}
                      onChange={(e) => setEditOrg({...editOrg, contact_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Email *</Label>
                    <Input
                      type="email"
                      value={editOrg.contact_email}
                      onChange={(e) => setEditOrg({...editOrg, contact_email: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={editOrg.description}
                    onChange={(e) => setEditOrg({...editOrg, description: e.target.value})}
                    rows={2}
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowEditOrgDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-gradient-to-r from-blue-500 to-green-500">
                    Save Changes
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {organizations.map((org) => (
              <Card key={org.id} className="card-hover" data-testid={`org-card-${org.short_name}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Building2 className="w-5 h-5 text-blue-500" />
                        {org.name}
                      </CardTitle>
                      <CardDescription>{org.short_name}</CardDescription>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditOrgDialog(org)} data-testid={`edit-org-${org.short_name}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteOrg(org.id, org.name)} data-testid={`delete-org-${org.short_name}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-3">{org.description}</p>
                  <div className="text-sm border-t pt-3">
                    <p><strong>Contact:</strong> {org.contact_name}</p>
                    <p className="text-gray-500 text-xs">{org.contact_email}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Report Status Tab */}
        <TabsContent value="reports" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Organization Report Submissions</CardTitle>
              <CardDescription>Track which organizations have submitted their monthly reports and send reminders</CardDescription>
            </CardHeader>
            <CardContent>
              {scheduledMeetings.filter(m => m.status !== 'completed').length === 0 ? (
                <p className="text-center text-gray-500 py-8">No upcoming meetings</p>
              ) : (
                <div className="space-y-6">
                  {scheduledMeetings.filter(m => m.status !== 'completed').slice(0, 5).map((meeting) => {
                    const pendingOrgs = organizations.filter(org => {
                      const status = getReportStatus(meeting.id, org.id);
                      return status.status !== 'submitted';
                    });
                    
                    return (
                      <div key={meeting.id} className="border rounded-lg p-4" data-testid={`report-status-meeting-${meeting.id}`}>
                        <div className="flex justify-between items-center mb-3">
                          <div>
                            <h4 className="font-semibold">{meeting.title}</h4>
                            <span className="text-sm text-gray-500">{formatDate(meeting.meeting_date)}</span>
                          </div>
                          {isAdmin && pendingOrgs.length > 0 && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-orange-600 border-orange-300 hover:bg-orange-50"
                              onClick={() => handleSendReminders(meeting.id, meeting.title, pendingOrgs.length)}
                              data-testid={`send-reminders-${meeting.id}`}
                            >
                              <Send className="w-4 h-4 mr-2" />
                              Send Reminders ({pendingOrgs.length})
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                          {organizations.map((org) => {
                            const reportStatus = getReportStatus(meeting.id, org.id);
                            return (
                              <div 
                                key={org.id} 
                                className={`flex items-center justify-between p-3 border rounded transition-all ${
                                  reportStatus.status === 'submitted' 
                                    ? 'bg-green-50 border-green-200' 
                                    : reportStatus.status === 'draft'
                                    ? 'bg-orange-50 border-orange-200'
                                    : 'bg-gray-50 border-gray-200'
                                }`}
                                data-testid={`report-status-${org.short_name}`}
                              >
                                <div className="flex items-center gap-2">
                                  {reportStatus.status === 'submitted' ? (
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                  ) : reportStatus.status === 'draft' ? (
                                    <Clock className="w-5 h-5 text-orange-500" />
                                  ) : (
                                    <AlertCircle className="w-5 h-5 text-gray-400" />
                                  )}
                                  <div>
                                    <span className="font-medium block">{org.short_name}</span>
                                    <span className={`text-xs ${reportStatus.color}`}>
                                      {reportStatus.label}
                                    </span>
                                  </div>
                                </div>
                                {isAdmin && reportStatus.status !== 'submitted' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0 text-orange-500 hover:text-orange-700 hover:bg-orange-100"
                                    onClick={() => handleSendSingleReminder(meeting.id, org.id, org.name)}
                                    title={`Send reminder to ${org.name}`}
                                    data-testid={`send-reminder-${org.short_name}`}
                                  >
                                    <Send className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
